import { useState, useEffect, useRef } from 'react';
import { useLiveData } from '@/hooks/useLiveData';

const steps = [
  { step: '01', title: '数据采集', desc: '多源异构数据接入与清洗', icon: 'ri-database-2-line', status: 'UP', freq: 28, freqUnit: 'GB/h' },
  { step: '02', title: '因子挖掘', desc: '机器学习驱动的新型因子发现', icon: 'ri-search-line', status: 'UP', freq: 512, freqUnit: '因子' },
  { step: '03', title: '策略建模', desc: '多策略框架构建与参数优化', icon: 'ri-node-tree', status: 'UP', freq: 52, freqUnit: '策略' },
  { step: '04', title: '回测验证', desc: '分布式回测与稳健性检验', icon: 'ri-test-tube-line', status: 'UP', freq: 8.2, freqUnit: 'TB' },
  { step: '05', title: '实盘部署', desc: '低延迟交易执行与实时监控', icon: 'ri-rocket-line', status: 'UP', freq: 3.9, freqUnit: 'ms' },
];

function ProcessStepCard({ step, index }: { step: typeof steps[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [pulseIdx, setPulseIdx] = useState(-1);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          // Sequential activation: each step activates with delay
          const delay = index * 250;
          const timer = setTimeout(() => setPulseIdx(index), delay);
          return () => clearTimeout(timer);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [index]);

  const isActive = pulseIdx >= index;

  return (
    <div
      ref={ref}
      className={`relative bg-background-200 rounded-xl border p-6 text-center transition-all duration-500 ${
        isActive
          ? 'border-accent-500/40 -translate-y-1 shadow-[0_0_25px_rgba(200,160,80,0.08)]'
          : 'border-background-300/30'
      } ${pulseIdx === index ? 'process-step-active' : ''}`}
    >
      {/* Step number with pulse */}
      <span className={`mono-data text-3xl font-bold transition-all duration-500 ${
        isActive ? 'text-accent-500/40' : 'text-primary-500/20'
      }`}>
        {step.step}
      </span>

      {/* Icon */}
      <div className={`w-10 h-10 mx-auto my-3 rounded-lg border flex items-center justify-center transition-all duration-500 ${
        isActive
          ? 'bg-accent-500/10 border-accent-500/25'
          : 'bg-primary-500/10 border-primary-500/15'
      }`}>
        <i className={`${step.icon} text-lg transition-colors duration-500 ${
          isActive ? 'text-accent-400' : 'text-primary-400'
        }`}></i>
      </div>

      <h4 className="text-sm font-semibold text-foreground-200 mb-1">{step.title}</h4>
      <p className="text-xs text-foreground-500 font-light mb-3">{step.desc}</p>

      {/* Live data badge */}
      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] mono-data transition-all duration-500 ${
        isActive
          ? 'bg-accent-500/10 border border-accent-500/20 text-accent-400'
          : 'bg-background-300/30 text-foreground-600'
      }`}>
        <span className={`w-1 h-1 rounded-full ${
          step.status === 'UP' ? 'bg-emerald-400 animate-pulse-glow' : 'bg-red-400'
        }`}></span>
        {visible ? step.freq : '--'}{step.freqUnit}
      </div>

      {/* Connecting dot for flow */}
      {index < steps.length - 1 && isActive && (
        <div className="hidden lg:block absolute top-1/2 -right-2.5 w-1.5 h-1.5 rounded-full bg-accent-400/60 process-dot-flow -translate-y-1/2 z-10"></div>
      )}
    </div>
  );
}

export default function QuantProcessSection() {
  return (
    <section className="py-20 md:py-28 bg-background-100">
      <div className="px-6 md:px-12 lg:px-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-accent-500 mb-3">Research Process</p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-100">研究流程</h2>
            {/* Pipeline throughput ticker */}
            <p className="mt-3 text-[10px] mono-data text-foreground-600">
              <span className="text-emerald-400/70">PIPELINE</span>
              <span className="mx-2 text-foreground-800">::</span>
              <span className="text-foreground-500">THROUGHPUT  </span>
              <span className="text-accent-400/80">2.4 TB/day</span>
              <span className="mx-2 text-foreground-800">|</span>
              <span className="text-foreground-500">LATENCY  </span>
              <span className="text-primary-400/80">3.9ms P99</span>
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {steps.map((step, i) => (
              <ProcessStepCard key={step.step} step={step} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}